import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, ShieldOffIcon, XCircleIcon } from 'lucide-react';
import { Widget } from '../Widget';
import { Button } from '../../ui/Button';
import { useGovernance } from '../../../contexts/GovernanceContext';
import { auditInScope } from '../../../utils/auditScope';
const RESULT = {
  succeeded: {
    Icon: CheckCircle2Icon,
    tone: 'text-ok-700',
    label: 'Completed'
  },
  denied: {
    Icon: ShieldOffIcon,
    tone: 'text-warn-700',
    label: 'Refused'
  },
  failed: {
    Icon: XCircleIcon,
    tone: 'text-danger-700',
    label: 'Failed'
  }
} as const;
function when(iso: string): string {
  const at = new Date(iso);
  const minutes = Math.round((Date.now() - at.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return at.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short'
  });
}

/**
 * The activity feed reads the append-only audit log — the same record the audit
 * page reads. It is not a second event system, and nothing is invented: a
 * refused attempt appears exactly like a successful one, because "who tried
 * what and was told no" is the more useful half of an operator's day.
 */
export function ActivityWidget() {
  const {
    audit,
    activeScope,
    scopeLabel
  } = useGovernance();
  const label = scopeLabel(activeScope);
  const visible = audit.filter(record => auditInScope(record.scope, activeScope, label));
  const shown = visible.slice(0, 6);
  return <Widget title="Recent activity" description="Attributed entries from the append-only audit log, within the scope you are looking at." state={shown.length ? 'ready' : 'empty'} emptyTitle="Nothing recorded in this scope" emptyDescription="Entries appear here as administrators act. Widen the scope you are looking at to see more." action={<Link to="/admin/audit" data-id="element-499" data-mp="386rttww3wkky">
          <Button variant="secondary" size="sm" data-id="element-500" data-mp="387uomfwjv765">
            Open audit log
          </Button>
        </Link>} footnote={visible.length > shown.length ? `Showing the 6 most recent of ${visible.length} entries in this scope.` : undefined} data-id="element-498" data-mp="xd5m74543nm2">
      <ul className="divide-y divide-line" data-id="element-501" data-mp="1m2m7xqygtjtp">
        {shown.map(record => {
        const {
          Icon,
          tone,
          label
        } = RESULT[record.result];
        return <li key={record.id} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0" data-id="element-502" data-mp="1v0662r117003">
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} aria-hidden="true" data-id="element-503" data-mp="1zz94vtqf49zm" />
              <div className="min-w-0 flex-1" data-id="element-504" data-mp="24qr5ggoj48k2">
                <p className="text-body-sm text-ink" data-id="element-505" data-mp="24t3ufdspfj6m">
                  <span className="font-medium" data-id="element-506" data-mp="29p2e4o338d0p">{record.action}</span>
                  {' — '}
                  <span className="text-ink-muted" data-id="element-507" data-mp="2dsgwvmiev7hu">{record.resource}</span>
                </p>
                <p className="mt-0.5 text-caption leading-5 text-ink-subtle" data-id="element-508" data-mp="2iq0l9f8tp0ni">
                  {label} · {record.actor} · {record.actorRole} · {record.scope}
                </p>
              </div>
              <span className="shrink-0 text-caption text-ink-subtle" data-id="element-509" data-mp="242kz4u4qnd6">{when(record.at)}</span>
            </li>;
      })}
      </ul>
    </Widget>;
}